import classnames from 'classnames';
import moment from 'moment';
import React from 'react';
import {Link} from 'react-router';
import type {InjectedRouter} from 'react-router';
import withRouter from 'react-router/lib/withRouter';

import {Icon, IconButton, Text} from '@albert-io/atomic';
import type {IconProp, IconStyle} from '@albert-io/atomic/atoms/Icon/Icon.react';
import {AssignmentMenuOptions} from './AssignmentMenuOptions';
import {history} from 'client/history';

import type {StatusSegment} from './constants';
import type {TeacherAssignmentRowFieldsFragment} from './queries/TeacherAssignments.queries.generated';

// "Due Tomorrow" reads relatively; far dates and ranges fall back to "MMM D".
const DUE_DATE_FORMATS = {
  sameDay: '[Today]',
  nextDay: '[Tomorrow]',
  nextWeek: 'MMM D',
  lastDay: '[Yesterday]',
  lastWeek: 'MMM D',
  sameElse: 'MMM D'
};

const formatDateTime = (date: string) => moment(date).format('MMM D, h:mm A');
const formatDateShort = (date: string) => moment(date).format('MMM D');
const formatEditedDate = (date: string) => moment(date).format('MMM D, YYYY');
const formatDueDate = (date: string) => moment(date).calendar(null, DUE_DATE_FORMATS);

// Due/start dates span the educator's in-scope (class, student) pairings, so cross-class
// rows show a min–max range and single-class rows collapse to one date.
const formatDateRange = (
  min: string,
  max: string | null | undefined,
  formatSingle: (date: string) => string
): string =>
  max && max !== min ? `${formatDateShort(min)} - ${formatDateShort(max)}` : formatSingle(min);

const getDueCopy = (minDueDate?: string | null, maxDueDate?: string | null): string =>
  minDueDate ? `Due ${formatDateRange(minDueDate, maxDueDate, formatDueDate)}` : '';

const getSubtitle = (
  assignment: TeacherAssignmentRowFieldsFragment,
  status: StatusSegment
): React.ReactNode => {
  if (status === 'draft') {
    return `Edited ${formatEditedDate(assignment.updatedAt)}`;
  }
  if (status === 'closed') {
    return 'Closed';
  }
  if (status === 'scheduled') {
    const {minStartDate, maxStartDate} = assignment;
    if (!minStartDate) {
      return 'Scheduled';
    }
    return `Scheduled for ${formatDateRange(minStartDate, maxStartDate, formatDateTime)}`;
  }
  // Open: "Due <date> • <completed> / <total> submissions". Counts are
  // (class, student) pairings, not distinct students.
  const parts: string[] = [];
  const dueCopy = getDueCopy(assignment.minDueDate, assignment.maxDueDate);
  if (dueCopy) {
    parts.push(dueCopy);
  }
  const total = assignment.countOfCompletedStudents + assignment.countOfIncompleteStudents;
  if (total > 0) {
    parts.push(`${assignment.countOfCompletedStudents} / ${total} submissions`);
  }
  if (!parts.length) {
    return 'Open';
  }
  // Margin-spaced bullet separator — more breathing room than a plain " • " string join.
  return parts.map((part, index) => (
    <React.Fragment key={part}>
      {index > 0 && <span className='mx-x1'>•</span>}
      {part}
    </React.Fragment>
  ));
};

const STATUS_DISPLAY: Record<
  StatusSegment,
  {color: 'tertiary' | 'warning'; icon?: IconProp; iconStyle?: IconStyle}
> = {
  open: {color: 'tertiary'},
  scheduled: {color: 'warning', icon: 'clock', iconStyle: 'light'},
  closed: {color: 'tertiary', icon: 'ban', iconStyle: 'regular'},
  draft: {color: 'tertiary'}
};

interface Props {
  /** The acting (possibly masqueraded) teacher — scopes reports and gates delete. */
  actorTeacherId: string;
  assignment: TeacherAssignmentRowFieldsFragment;
  /** Adds the classroom dimension to the report link (class-scoped list). */
  classroomId?: string;
  onDeleteComplete: () => void;
  router: InjectedRouter;
  /** The list segment the row was fetched under. */
  status: StatusSegment;
}

const AssignmentRowBase = ({
  actorTeacherId,
  assignment,
  classroomId,
  onDeleteComplete,
  router,
  status
}: Props) => {
  const isDraft = status === 'draft';
  const editPath = `/assignment-builder/create-assignment/${assignment.id}/settings${
    isDraft ? '/draft' : ''
  }`;
  const reportPath = actorTeacherId
    ? `/reports/teachers/${actorTeacherId}${
        classroomId ? `/classrooms/${classroomId}` : ''
      }/assignments/${assignment.id}/students`
    : null;
  const isCreator = Boolean(actorTeacherId) && assignment.teacherId === actorTeacherId;
  // Class-scoped delete only unassigns this class (open to its teachers); cross-class
  // delete removes the assignment entirely, so it's creator-only.
  const canEdit = (Boolean(classroomId) && !isDraft) || isCreator;

  const subtitle = getSubtitle(assignment, status);
  const {color: statusColor, icon: statusIcon, iconStyle: statusIconStyle} = STATUS_DISPLAY[status];

  const goToBuilder = () => history.pushState(null, editPath);

  return (
    // Clicking anywhere on the row opens the builder. The name stays a real <Link> so keyboard/AT users
    // and middle-click/open-in-new-tab still work — the row onClick is a mouse-only convenience on top of
    // it (hence the a11y disable). The action cluster stops propagation so its buttons don't also navigate.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      className='flex cursor-pointer items-center gap-x2 rounded-x1 p-x1 hover:background-primary-hover active:background-primary-pressed'
      onClick={goToBuilder}
    >
      <div
        className={classnames(
          'flex h-x5 w-x5 shrink-0 items-center justify-center rounded-full',
          isDraft ? 'border border-dashed border-border-bold' : 'background-brand'
        )}
      >
        <Icon
          className='text-m'
          color={isDraft ? 'tertiary' : 'primary-inverse-static'}
          icon='file-lines'
          iconStyle='regular'
        />
      </div>
      <div className='min-w-0 flex grow flex-col'>
        <Link className='block truncate text-m content-primary no-underline' to={editPath}>
          {assignment.name}
        </Link>
        {subtitle ? (
          <Text as='div' className='truncate' color={statusColor} size='s'>
            {statusIcon ? (
              <Icon
                className='mr-x0-half text-xs!'
                color={statusColor}
                icon={statusIcon}
                iconStyle={statusIconStyle}
              />
            ) : null}
            {subtitle}
          </Text>
        ) : null}
      </div>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
      <div className='flex shrink-0 items-center gap-x0-half' onClick={(e) => e.stopPropagation()}>
        {!isDraft && reportPath && (
          <IconButton
            className='relative'
            icon='chart-line'
            iconStyle='regular'
            label='View report'
            tooltip='View report'
            onClick={() => history.pushState(null, reportPath)}
            size='s'
          />
        )}
        <IconButton
          className='relative'
          icon='pencil'
          iconStyle='regular'
          label='Edit'
          tooltip='Edit'
          onClick={() => history.pushState(null, editPath)}
          size='s'
        />
        <span className='relative inline-flex'>
          <AssignmentMenuOptions
            assignmentId={assignment.id}
            assignmentName={assignment.name}
            isDraft={isDraft}
            canEdit={canEdit}
            classroomId={classroomId}
            reportPath={reportPath}
            updatedAt={assignment.updatedAt}
            onDeleteComplete={onDeleteComplete}
            router={router}
          />
        </span>
      </div>
    </div>
  );
};

export const AssignmentRow = withRouter(AssignmentRowBase);
